import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Package,
  User,
  Mail,
  Phone,
  Calendar,
  ShoppingCart
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

const ToolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const { data: detailData } = await axios.get(`${API}/tools/${id}/assignments`);
      setData(detailData);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      navigate('/tools');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  const tool = data.tool;
  const assignments = data.assignments;

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/tools')}
            variant="outline"
            size="icon"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">
              {tool.name}
            </h1>
            <p className="text-muted-foreground mt-2">Détails de stock et affectations</p>
          </div>
        </div>
      </div>

      {/* Tool Info Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading">Informations de l'outil</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6">
              {tool.photo ? (
                <img
                  src={tool.photo}
                  alt={tool.name}
                  className="w-32 h-32 object-cover rounded-lg border"
                />
              ) : (
                <div className="w-32 h-32 bg-muted rounded-lg flex items-center justify-center">
                  <Package className="w-16 h-16 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 space-y-3">
                <div>
                  <h2 className="text-2xl font-heading font-bold">{tool.name}</h2>
                  <p className="text-muted-foreground">{tool.type}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">N° Article:</span>
                    <p className="font-medium">{tool.article_number}</p>
                  </div>
                  {tool.order_number && (
                    <div>
                      <span className="text-muted-foreground">N° Commande:</span>
                      <p className="font-medium">{tool.order_number}</p>
                    </div>
                  )}
                  {tool.supplier && (
                    <div>
                      <span className="text-muted-foreground">Fournisseur:</span>
                      <p className="font-medium">{tool.supplier}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              État du Stock
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm font-medium">Stock Total</span>
                <Badge variant="outline" className="text-lg px-3">
                  {tool.quantity_total}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-chart-3/10 rounded-lg">
                <span className="text-sm font-medium">Disponibles</span>
                <Badge className="bg-chart-3 text-lg px-3">
                  {tool.quantity_available}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                <span className="text-sm font-medium">Affectés</span>
                <Badge variant="secondary" className="text-lg px-3">
                  {tool.quantity_assigned}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Assignments */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-heading font-bold">Affectations Actives</h2>
          <Badge variant="default" className="text-base px-4 py-1">
            {assignments.length} affectation{assignments.length > 1 ? 's' : ''}
          </Badge>
        </div>

        {assignments.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {assignments.map((assignment) => (
              <Card
                key={assignment.id}
                data-testid={`assignment-card-${assignment.id}`}
                className="hover:shadow-lg transition-shadow duration-200"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {assignment.collaborator?.name?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <CardTitle className="text-lg font-heading">
                          {assignment.collaborator?.name}
                        </CardTitle>
                        {assignment.collaborator?.email && (
                          <p className="text-sm text-muted-foreground">{assignment.collaborator.email}</p>
                        )}
                      </div>
                    </div>
                    <Badge variant="default" className="text-base px-3">
                      x{assignment.quantity}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {assignment.collaborator?.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{assignment.collaborator.phone}</span>
                    </div>
                  )}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Affecté le:</span>
                      <span>{formatDate(assignment.assigned_date)}</span>
                    </div>
                    {assignment.end_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Fin prévue:</span>
                        <span>{formatDate(assignment.end_date)}</span>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => navigate(`/collaborators/${assignment.collaborator_id}`)}
                    variant="outline"
                    size="sm"
                    className="w-full rounded-full"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Voir le profil
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-2 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <User className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune affectation active pour cet outil</p>
              <p className="text-sm text-muted-foreground mt-2">
                {tool.quantity_available} unité{tool.quantity_available > 1 ? 's' : ''} disponible{tool.quantity_available > 1 ? 's' : ''} en stock
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ToolDetail;